import { useState, useEffect, useCallback } from 'react';
import supabase from '../../supabase-client';

export const useBibleDetail = (bibleId) => {
  const [bible, setBible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBible = useCallback(async () => {
    if (!bibleId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('biblebook')
        .select('*')
        .eq('id_bible', bibleId)
        .single();

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (!data) {
        throw new Error('Biblia no encontrada');
      }

      // Map the data to a more user-friendly format
      const mappedBible = {
        id: data.id_bible,
        title: data.title_bible,
        version: data.version_bible || 'Versión no especificada',
        authorName: data.version_bible,
        description: data.description_bible,
        price: data.price_bible,
        price_cover: data.price_cover || 0,
        total_with_cover: data.total_with_cover || 0,
        price_tab_index: data.price_tab_index || 0,
        total_with_tab_index: data.total_with_tab_index || 0,
        total_with_both: data.total_with_both || 0,
        originalPrice: data.price_bible,
        available: data.available_bible,
        has_cover: data.has_cover,
        has_tab_index: data.has_tab_index,
        stock: data.stock_bible || 0,
        rating: data.rating_bible || 0,
        images: data.image_urls || [],
        language: data.language_bible,
        format: data.format_bible,
        publisher: data.publisher_bible || '',
        pages: data.pages_bible,
        features: [],
        characters_bible: data.characters_bible || [],
        isNew: data.is_new_bible || false,
        favoriteCount: data.favorite_count_product || 0,
        favorite_product: data.favorite_product
      };

      setBible(mappedBible);
    } catch (err) {
      console.error('Error fetching bible:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bibleId]);

  const refreshBible = useCallback(() => {
    fetchBible();
  }, [fetchBible]);

  const toggleFavorite = useCallback(async (isFavorite) => {
    if (!bible?.id) return false;

    try {
      // This would integrate with your favorites system
      // For now, just simulate the API call
      console.log(`Toggle favorite for bible ${bible.id}: ${isFavorite}`);
      return true;
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return false;
    }
  }, [bible?.id]);

  const fetchRelatedBibles = useCallback(async () => {
    if (!bible) return [];

    try {
      const { data, error: fetchError } = await supabase
        .from('biblebook')
        .select(`
          id_bible,
          title_bible,
          price_bible,
          available_bible,
          image_urls,
          version_bible
        `)
        .neq('id_bible', bible.id)
        .limit(8);

      if (fetchError) {
        console.error('Error fetching related bibles:', fetchError);
        return [];
      }

      return (data || []).map(item => ({
        id: item.id_bible,
        title: item.title_bible,
        price: item.price_bible,
        available: item.available_bible,
        image: item.image_urls?.[0],
        version: item.version_bible
      }));
    } catch (error) {
      console.error('Error fetching related bibles:', error);
      return [];
    }
  }, [bible]);

  useEffect(() => {
    fetchBible();
  }, [fetchBible]);

  return {
    bible,
    loading,
    error,
    refreshBible,
    toggleFavorite,
    fetchRelatedBibles
  };
};

